import { FoquzComponent } from 'Models/foquz-component';
import { fitImageToAvailableWidth } from 'Utils/image/fit-to-available-width';

const CONTAINER_CLASS = 'image-click-areas__container';
const RESIZE_HANDLE_CLASS = 'image-click-areas__resize-handle';

export class ViewModel extends FoquzComponent {
  constructor(params, element) {
    super(params, element);

    // Parameters from parent
    this.imageUrl = params.imageUrl;
    this.areas = params.areas || ko.observableArray([]);
    this.onAreasChange = params.onAreasChange;
    this.parentSelectedAreaObservable = params.selectedAreaObservable;
    this.augmentAreaWithPixelComputeds = params.augmentAreaWithPixelComputeds;
    this.originalImageDimensions = params.originalImageDimensions || ko.observable({ width: 0, height: 0 });
    this.displayedImageDimensionsOutput = params.displayedImageDimensionsOutput;
    this.ancestorContextElement = params.ancestorContextElement;
    this.isEditingLocked = ko.observable(ko.unwrap(params.isEditingLocked) || false);
    this.isFullBlocked = ko.observable(ko.unwrap(params.isFullBlocked) || false);
    this.registerUnlockedArea = typeof params.registerUnlockedArea === 'function' ? params.registerUnlockedArea : null;
    this.canEditAreaFn = typeof params.canEditArea === 'function' ? params.canEditArea : null;
    this.isAreaLockedFn = typeof params.isAreaEditingLocked === 'function' ? params.isAreaEditingLocked : null;
    this.onBlockedInteraction = params.onBlockedInteraction || function() { console.warn('onBlockedInteraction not provided to image-click-areas'); };

    this.canEditArea = (area) => {
      if (this.isFullBlocked()) {
        return false;
      }
      if (this.canEditAreaFn && typeof this.canEditAreaFn === 'function') {
        return !!this.canEditAreaFn(area);
      }
      if (!this.isEditingLocked()) {
        return true;
      }
      const editableFlag = area ? ko.unwrap(area.isEditable) : false;
      return !!editableFlag;
    };
    this.isAreaEditingLocked = (area) => {
      if (this.isFullBlocked()) {
        return true;
      }
      if (this.isAreaLockedFn && typeof this.isAreaLockedFn === 'function') {
        return !!this.isAreaLockedFn(area);
      }
      if (!this.isEditingLocked()) {
        return false;
      }
      return !this.canEditArea(area);
    };

    // Internal state
    this.displayedImageDimensions = ko.observable({ width: 0, height: 0 });
    this.imageOffset = ko.observable({ x: 0, y: 0 });
    this.selectedArea = ko.observable(null);
    this.isDrawing = ko.observable(false);
    this.drawingArea = ko.observable(null);
    this.isContainerLocked = ko.computed(() => {
      if (this.isFullBlocked()) {
        return true;
      }
      if (!this.isEditingLocked()) {
        return false;
      }
      const currentAreas = ko.isObservable(this.areas) ? this.areas() : this.areas;
      if (!currentAreas || typeof currentAreas.length === 'undefined' || currentAreas.length === 0) {
        return false;
      }
      return !currentAreas.some((area) => this.canEditArea(area));
    });

    // Drawing state
    this.drawStartX = 0;
    this.drawStartY = 0;
    this.currentDrawX = 0;
    this.currentDrawY = 0;

    this.isDragging = ko.observable(false);
    this.isResizing = ko.observable(false);
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.resizeHandle = null;
    this.lastDragPixelPosition = { x: 0, y: 0 };

    this.containerElement = null;
    this.viewportElement = null;
    this.imageElement = null;

    this.initializeComponent();
    this.setupEventHandlers();

    this.displayedImageDimensions.subscribe((newDimensions) => {
      if (this.displayedImageDimensionsOutput && ko.isObservable(this.displayedImageDimensionsOutput)) {
        this.displayedImageDimensionsOutput(newDimensions);
      }
    });
  }

  initializeComponent() {
    setTimeout(() => {
      this.containerElement = this.element.querySelector(`.${CONTAINER_CLASS}`);
      this.viewportElement = this.element.closest('.image-container') || this.element.parentElement;
      if (this.containerElement) {
        this.setupResizeObserver();
        this.setupDocumentEventListeners();
        this.updateDisplayedDimensions();
      }
    }, 0);

    if (ko.isObservable(this.originalImageDimensions)) {
      this.originalImageDimensions.subscribe(() => {
        this.updateDisplayedDimensions();
      });
    }

    this.areas().forEach((area, index) => {
      if (!area.pixelWidth || !area.pixelHeight) {
        if (this.augmentAreaWithPixelComputeds) {
          this.augmentAreaWithPixelComputeds(area);
        }
      }
    });

    if (ko.isObservable(this.imageUrl)) {
      this.imageUrl.subscribe(() => {
        this.updateDisplayedDimensions();
      });
    }

    if (this.parentSelectedAreaObservable) {
      this.parentSelectedAreaObservable.subscribe((newlySelectedAreaFromParent) => {
        if (this.selectedArea() !== newlySelectedAreaFromParent) {
          this.areas().forEach(a => {
            if (ko.isObservable(a.isSelected)) {
              a.isSelected(a === newlySelectedAreaFromParent);
            } else {
              console.warn("Area 'isSelected' property is not observable:", a);
            }
          });
          this.selectedArea(newlySelectedAreaFromParent);
        }
      });
      const initialParentSelection = this.parentSelectedAreaObservable();
      if (initialParentSelection) {
          this.selectedArea(initialParentSelection);
          this.areas().forEach(a => {
            if (ko.isObservable(a.isSelected)) {
                a.isSelected(a === initialParentSelection);
            }
          });
      }
    }
    
    if (ko.isObservable(this.areas)) {
        this.areas.subscribe((newArray) => {
            const currentSelection = this.selectedArea();
            newArray.forEach((area, index) => {
                if (ko.isObservable(area.isSelected)) {
                    area.isSelected(area === currentSelection);
                }
                if (!area.pixelWidth || !area.pixelHeight) {
                  if (this.augmentAreaWithPixelComputeds) {
                    this.augmentAreaWithPixelComputeds(area);
                  }
                }
            });
        });
    }
  }

  setupDocumentEventListeners() {
    this.boundHandleDocumentMouseMove = this.handleDocumentMouseMove.bind(this);
    this.boundHandleDocumentMouseUp = this.handleDocumentMouseUp.bind(this);
    this.boundHandleDragMove = this.handleDragMove.bind(this);
    this.boundHandleDragEnd = this.handleDragEnd.bind(this);
    this.boundHandleResizeMove = this.handleResizeMove.bind(this);
    this.boundHandleResizeEnd = this.handleResizeEnd.bind(this);
  }

  setupResizeObserver() {
    const observed = this.viewportElement || this.containerElement;
    if (!observed) return;

    const resizeObserver = new ResizeObserver(() => {
      this.updateDisplayedDimensions();
    });

    resizeObserver.observe(observed);
  }

  updateDisplayedDimensions() {
    if (!this.containerElement) return;

    const original = this.originalImageDimensions();
    const viewport = this.viewportElement || this.containerElement;
    const availableWidth = viewport.clientWidth;
    
    if (original.width === 0 || original.height === 0) {
      // If original dimensions are not set, reset displayed and output
      this.displayedImageDimensions({ width: 0, height: 0 });
      this.imageOffset({ x: 0, y: 0 });
      if (this.displayedImageDimensionsOutput && ko.isObservable(this.displayedImageDimensionsOutput)) {
        this.displayedImageDimensionsOutput({ width: 0, height: 0 });
      }
      return;
    }

    const fitted = fitImageToAvailableWidth(original, availableWidth);
    const newDisplayedDimensions = {
      width: fitted.width,
      height: fitted.height
    };
    this.displayedImageDimensions(newDisplayedDimensions);
    this.imageOffset({ x: 0, y: 0 });
  }

  setupEventHandlers() {
    document.addEventListener('keydown', (e) => {
      if (this.isFullBlocked()) return;

      const selectedArea = this.selectedArea();
      if (!this.canEditArea(selectedArea)) return;
      const activeElement = document.activeElement;
      if (activeElement) {
        const tagName = activeElement.tagName.toUpperCase();
        if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
          if (this.ancestorContextElement && this.ancestorContextElement.contains(activeElement)) {
            return;
          }
        }
      }

      if (!selectedArea && e.key === 'Delete') return;
      if (!selectedArea) return;

      if (e.key === 'Delete') {
        this.deleteArea(selectedArea);
      } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        this.moveAreaByArrowKey(selectedArea, e.key);
      }
    });
  }

  moveAreaByArrowKey(area, key) {
    if (!this.canEditArea(area)) return;

    const displayed = this.displayedImageDimensions();
    if (displayed.width === 0 || displayed.height === 0) return;

    const stepX = 0.001;
    const stepY = 0.001;

    let currentX = area.x();
    let currentY = area.y();
    const currentWidth = area.width();
    const currentHeight = area.height();

    switch (key) {
      case 'ArrowUp':
        currentY = Math.max(0, currentY - stepY);
        break;
      case 'ArrowDown':
        currentY = Math.min(1 - currentHeight, currentY + stepY);
        break;
      case 'ArrowLeft':
        currentX = Math.max(0, currentX - stepX);
        break;
      case 'ArrowRight':
        currentX = Math.min(1 - currentWidth, currentX + stepX);
        break;
    }

    area.x(currentX);
    area.y(currentY);

    console.log('[IMAGE-CLICK-AREAS] moveAreaByArrowKey:',
                { key, newX: currentX, newY: currentY, width: currentWidth, height: currentHeight });

    this.notifyAreasChange();
  }

  calculatePixelX(proportionalX) {
    const displayed = this.displayedImageDimensions();
    return (proportionalX || 0) * displayed.width;
  }

  calculatePixelY(proportionalY) {
    const displayed = this.displayedImageDimensions();
    return (proportionalY || 0) * displayed.height;
  }

  calculatePixelWidth(proportionalWidth) {
    const displayed = this.displayedImageDimensions();
    return (proportionalWidth || 0) * displayed.width;
  }

  calculatePixelHeight(proportionalHeight) {
    const displayed = this.displayedImageDimensions();
    return (proportionalHeight || 0) * displayed.height;
  }

  pixelToProportionalX(pixelX) {
    const displayed = this.displayedImageDimensions();
    return displayed.width > 0 ? pixelX / displayed.width : 0;
  }

  pixelToProportionalY(pixelY) {
    const displayed = this.displayedImageDimensions();
    return displayed.height > 0 ? pixelY / displayed.height : 0;
  }

  pixelToProportionalWidth(pixelWidth) {
    const displayed = this.displayedImageDimensions();
    return displayed.width > 0 ? pixelWidth / displayed.width : 0;
  }

  pixelToProportionalHeight(pixelHeight) {
    const displayed = this.displayedImageDimensions();
    return displayed.height > 0 ? pixelHeight / displayed.height : 0;
  }

  getAreaNameStyles(area) {
    const areaY = this.calculatePixelY(ko.unwrap(area.y()));
    const areaHeight = this.calculatePixelHeight(ko.unwrap(area.height()));
    const areaX = this.calculatePixelX(ko.unwrap(area.x()));
    const imageOffset = this.imageOffset();
    const displayedDimensions = this.displayedImageDimensions();
    
    const labelHeight = 24;
    const labelWidth = 224;
    const labelPadding = 9;
    const labelOffset = 3;
    
    const wouldOverflowBottom = areaY + areaHeight + imageOffset.y + labelHeight > 
                               displayedDimensions.height + imageOffset.y * 2;
    const wouldOverflowRight = areaX + imageOffset.x + labelWidth > 
                              displayedDimensions.width + imageOffset.x * 2;
    
    return {
      top: wouldOverflowBottom ? 'auto' : (areaHeight + labelPadding) + 'px',
      bottom: wouldOverflowBottom ? (areaHeight + labelPadding) + 'px' : 'auto',
      left: wouldOverflowRight ? 'auto' : (-labelOffset) + 'px',
      right: wouldOverflowRight ? (-labelOffset) + 'px' : 'auto'
    };
  }

  handleMouseDown(_, event) {
    if (this.isFullBlocked()) return;

    if (event.target.classList.contains(CONTAINER_CLASS)) {
      event.preventDefault();
      this.startDrawing(event);
    }
  }

  startDrawing(event) {
    if (this.isFullBlocked()) return;

    if (!this.containerElement) return;

    const rect = this.containerElement.getBoundingClientRect();
    const displayed = this.displayedImageDimensions();

    const imageOffsetX = (rect.width - displayed.width) / 2;
    const imageOffsetY = (rect.height - displayed.height) / 2;

    const mouseXInContainer = event.clientX - rect.left;
    const mouseYInContainer = event.clientY - rect.top;
    
    let imageRelativeX = mouseXInContainer - imageOffsetX;
    let imageRelativeY = mouseYInContainer - imageOffsetY;

    this.drawStartX = Math.max(0, Math.min(imageRelativeX, displayed.width - 10));
    this.drawStartY = Math.max(0, Math.min(imageRelativeY, displayed.height - 10));

    this.currentDrawX = this.drawStartX;
    this.currentDrawY = this.drawStartY;

    this.isDrawing(true);
    this.selectedArea(null);

    console.log('[IMAGE-CLICK-AREAS] startDrawing:', { sx: this.drawStartX, sy: this.drawStartY, displayed: ko.toJS(this.displayedImageDimensions()) });

    document.addEventListener('mousemove', this.boundHandleDocumentMouseMove);
    document.addEventListener('mouseup', this.boundHandleDocumentMouseUp);

    this.updateDrawingArea();
  }

  handleMouseMove() {
  }

  handleDocumentMouseMove(event) {
    if (this.isDrawing()) {
      this.updateDrawing(event);
    }
  }

  updateDrawing(event) {
    if (!this.containerElement) return;

    const rect = this.containerElement.getBoundingClientRect();
    const displayed = this.displayedImageDimensions();

    const imageOffsetX = (rect.width - displayed.width) / 2;
    const imageOffsetY = (rect.height - displayed.height) / 2;

    const mouseXInContainer = event.clientX - rect.left;
    const mouseYInContainer = event.clientY - rect.top;
    
    let imageRelativeX = mouseXInContainer - imageOffsetX;
    let imageRelativeY = mouseYInContainer - imageOffsetY;

    // Constrain to image bounds
    this.currentDrawX = Math.max(0, Math.min(imageRelativeX, displayed.width));
    this.currentDrawY = Math.max(0, Math.min(imageRelativeY, displayed.height));

    this.updateDrawingArea();
  }

  updateDrawingArea() {
    const left = Math.min(this.drawStartX, this.currentDrawX);
    const top = Math.min(this.drawStartY, this.currentDrawY);
    const width = Math.abs(this.currentDrawX - this.drawStartX);
    const height = Math.abs(this.currentDrawY - this.drawStartY);

    const currentImageOffset = this.imageOffset();
    this.drawingArea({
      left: currentImageOffset.x + left,
      top: currentImageOffset.y + top,
      width: width,
      height: height
    });
    console.log('[IMAGE-CLICK-AREAS] updateDrawingArea:', { drawingArea: ko.toJS(this.drawingArea()) });
  }

  handleMouseUp() {
    // This is for the container mouseup - not used for drawing
    // Drawing uses document mouseup for better tracking
  }

  handleDocumentMouseUp() {
    if (this.isDrawing()) {
      this.finishDrawing();
    }
  }

  finishDrawing() {
    // Clean up document event listeners
    document.removeEventListener('mousemove', this.boundHandleDocumentMouseMove);
    document.removeEventListener('mouseup', this.boundHandleDocumentMouseUp);

    const drawing = this.drawingArea();
    const currentImageOffset = this.imageOffset();

    console.log('[IMAGE-CLICK-AREAS] finishDrawing - Drawing:', ko.toJS(drawing));

    if (drawing && drawing.width >= 10 && drawing.height >= 10) {
      const newArea = {
        id: this.generateAreaId(),
        name: ko.observable(''),
        x: ko.observable(this.pixelToProportionalX(drawing.left - currentImageOffset.x)),
        y: ko.observable(this.pixelToProportionalY(drawing.top - currentImageOffset.y)),
        width: ko.observable(this.pixelToProportionalWidth(drawing.width)),
        height: ko.observable(this.pixelToProportionalHeight(drawing.height)),
        isSelected: ko.observable(false),
        clickCount: ko.observable(0)
      };
      
      let px = newArea.x();
      let py = newArea.y();
      let pw = newArea.width();
      let ph = newArea.height();

      px = Math.max(0, Math.min(px, 1));
      py = Math.max(0, Math.min(py, 1));
      pw = Math.max(0.001, Math.min(pw, 1 - px));
      ph = Math.max(0.001, Math.min(ph, 1 - py));

      newArea.x(px);
      newArea.y(py);
      newArea.width(pw);
      newArea.height(ph);

      console.log('[IMAGE-CLICK-AREAS] finishDrawing - New Area (proportional):', 
                  { x: px, y: py, w: pw, h: ph, originalDrawing: ko.toJS(drawing) });

      const augmentedArea = this.augmentAreaWithPixelComputeds(newArea);
      this.areas.push(augmentedArea); // Push directly after augmentation
      if (this.registerUnlockedArea) {
        this.registerUnlockedArea(augmentedArea);
      }

      if (this.parentSelectedAreaObservable) {
        this.parentSelectedAreaObservable(augmentedArea); 
      } else {
        this.selectedArea(augmentedArea);
        augmentedArea.isSelected(true);
      }
      this.notifyAreasChange();
    }

    this.isDrawing(false);
    this.drawingArea(null);
  }

  generateAreaId() {
    return 'area_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
  }

  // Area interaction handlers
  onAreaMouseDown(area, _, event) {
    if (this.isFullBlocked()) return;

    event.stopPropagation();
    
    this.blurFocusedInputs();
    
    this.selectedArea(area);

    if (this.parentSelectedAreaObservable) {
      this.parentSelectedAreaObservable(area);
    }

    this.areas().forEach(a => {
      if (ko.isObservable(a.isSelected)) {
        a.isSelected(a === area);
      }
    });

    if (!this.canEditArea(area)) {
      this.onBlockedInteraction();
      return;
    }

    if (event.target.classList.contains(RESIZE_HANDLE_CLASS)) {
      this.startResize(area, event);
    } else {
      this.startDrag(area, event);
    }
  }

  blurFocusedInputs() {
    const activeElement = document.activeElement;
    if (activeElement && activeElement !== document.body) {
      const tagName = activeElement.tagName.toUpperCase();
      if (tagName === 'INPUT' || tagName === 'TEXTAREA' || activeElement.contentEditable === 'true') {
        activeElement.blur();
      }
    }
  }

  startDrag(area, event) {
    if (!this.canEditArea(area)) return;

    this.isDragging(true);
    this.draggedArea = area;

    const rect = this.containerElement.getBoundingClientRect();
    const displayed = this.displayedImageDimensions();
    const imageOffsetX = (rect.width - displayed.width) / 2;
    const imageOffsetY = (rect.height - displayed.height) / 2;

    this.dragStartX = event.clientX - rect.left - imageOffsetX;
    this.dragStartY = event.clientY - rect.top - imageOffsetY;

    const initialAreaPixelX = this.calculatePixelX(area.x());
    const initialAreaPixelY = this.calculatePixelY(area.y());

    this.dragAreaStartX = initialAreaPixelX;
    this.dragAreaStartY = initialAreaPixelY;

    this.lastDragPixelPosition = { x: initialAreaPixelX, y: initialAreaPixelY };

    console.log('[IMAGE-CLICK-AREAS] startDrag:',
                { area: ko.toJS(area), mouseStart: {x: this.dragStartX, y: this.dragStartY},
                  areaInitialPixel: {x: initialAreaPixelX, y: initialAreaPixelY} });

    document.addEventListener('mousemove', this.boundHandleDragMove);
    document.addEventListener('mouseup', this.boundHandleDragEnd);
  }

  handleDragMove(event) {
    if (!this.isDragging() || !this.draggedArea) return;

    const rect = this.containerElement.getBoundingClientRect();
    const displayed = this.displayedImageDimensions();
    const imageOffsetX = (rect.width - displayed.width) / 2;
    const imageOffsetY = (rect.height - displayed.height) / 2;

    const currentX = event.clientX - rect.left - imageOffsetX;
    const currentY = event.clientY - rect.top - imageOffsetY;

    const deltaX = currentX - this.dragStartX;
    const deltaY = currentY - this.dragStartY;

    const newX = this.dragAreaStartX + deltaX;
    const newY = this.dragAreaStartY + deltaY;

    const areaWidth = this.calculatePixelWidth(this.draggedArea.width());
    const areaHeight = this.calculatePixelHeight(this.draggedArea.height());

    const constrainedX = Math.max(0, Math.min(newX, displayed.width - areaWidth));
    const constrainedY = Math.max(0, Math.min(newY, displayed.height - areaHeight));

    this.lastDragPixelPosition = { x: constrainedX, y: constrainedY };

    this.draggedArea.x(this.pixelToProportionalX(constrainedX));
    this.draggedArea.y(this.pixelToProportionalY(constrainedY));

    console.log('[IMAGE-CLICK-AREAS] handleDragMove:', 
                { deltaX, deltaY, newX, newY, constrainedX, constrainedY, 
                  propX: this.draggedArea.x(), propY: this.draggedArea.y() });
  }

  handleDragEnd() {
    if (!this.draggedArea) return;

    this.isDragging(false);

    document.removeEventListener('mousemove', this.boundHandleDragMove);
    document.removeEventListener('mouseup', this.boundHandleDragEnd);

    this.updateDisplayedDimensions();

    const finalX = this.pixelToProportionalX(this.lastDragPixelPosition.x);
    const finalY = this.pixelToProportionalY(this.lastDragPixelPosition.y);

    this.draggedArea.x(finalX);
    this.draggedArea.y(finalY);

    console.log('[IMAGE-CLICK-AREAS] handleDragEnd - Final Proportional:', { x: finalX, y: finalY, lastPixel: this.lastDragPixelPosition });

    this.notifyAreasChange();
    this.draggedArea = null;
  }

  startResize(area, event) {
    if (!this.canEditArea(area)) return;

    this.isResizing(true);
    this.resizedArea = area;
    this.resizeHandle = event.target.getAttribute('data-handle');

    const rect = this.containerElement.getBoundingClientRect();
    const displayed = this.displayedImageDimensions();
    const imageOffsetX = (rect.width - displayed.width) / 2;
    const imageOffsetY = (rect.height - displayed.height) / 2;

    this.resizeStartX = event.clientX - rect.left - imageOffsetX;
    this.resizeStartY = event.clientY - rect.top - imageOffsetY;

    this.resizeAreaStartX = this.calculatePixelX(area.x());
    this.resizeAreaStartY = this.calculatePixelY(area.y());
    this.resizeAreaStartWidth = this.calculatePixelWidth(area.width());
    this.resizeAreaStartHeight = this.calculatePixelHeight(area.height());

    document.addEventListener('mousemove', this.boundHandleResizeMove);
    document.addEventListener('mouseup', this.boundHandleResizeEnd);
  }

  handleResizeMove(event) {
    if (!this.isResizing() || !this.resizedArea) return;

    const rect = this.containerElement.getBoundingClientRect();
    const displayed = this.displayedImageDimensions();
    const imageOffsetX = (rect.width - displayed.width) / 2;
    const imageOffsetY = (rect.height - displayed.height) / 2;

    const currentX = event.clientX - rect.left - imageOffsetX;
    const currentY = event.clientY - rect.top - imageOffsetY;

    const deltaX = currentX - this.resizeStartX;
    const deltaY = currentY - this.resizeStartY;

    this.applyResize(deltaX, deltaY);
  }

  applyResize(deltaX, deltaY) {
    const displayed = this.displayedImageDimensions();
    let newX = this.resizeAreaStartX;
    let newY = this.resizeAreaStartY;
    let newWidth = this.resizeAreaStartWidth;
    let newHeight = this.resizeAreaStartHeight;

    const handle = this.resizeHandle;
    const minSize = 10;

    if (handle.includes('n')) { // n – верхняя граница
      newY = this.resizeAreaStartY + deltaY;
      newHeight = this.resizeAreaStartHeight - deltaY;
    }
    if (handle.includes('s')) { // s – нижняя граница
      newHeight = (handle.includes('n') ? (this.resizeAreaStartY + this.resizeAreaStartHeight) : (this.resizeAreaStartY + this.resizeAreaStartHeight + deltaY)) - newY;
    }
    if (handle.includes('w')) { // w – левая граница
      newX = this.resizeAreaStartX + deltaX;
      newWidth = this.resizeAreaStartWidth - deltaX;
    }
    if (handle.includes('e')) { // e – правая граница
      newWidth = (handle.includes('w') ? (this.resizeAreaStartX + this.resizeAreaStartWidth) : (this.resizeAreaStartX + this.resizeAreaStartWidth + deltaX)) - newX;
    }

    // 2. Добавляем ограничения
    // X-axis 
    if (handle.includes('w')) { // Левая граница
      const originalRightX = this.resizeAreaStartX + this.resizeAreaStartWidth;
      newX = Math.max(0, newX); // Ограничиваем X так, чтобы было >= 0
      newX = Math.min(newX, originalRightX - minSize);
      newWidth = originalRightX - newX; // Пересчитываем ширину
    } else if (handle.includes('e')) { // Правая граница
      newWidth = Math.max(minSize, newWidth); // Ширина должна быть не меньше minSize
      // Ограничиваем ширину так, чтобы newX + newWidth <= displayed.width
      newWidth = Math.min(newWidth, displayed.width - newX);
    } else { // Нет активного горизонтального изменения (например, только 'n' или 's' handle)
      newWidth = Math.max(minSize, newWidth);
      newWidth = Math.min(newWidth, displayed.width - newX);
      if (newX + newWidth > displayed.width) {
        newX = displayed.width - newWidth; // Сдвигаем X влево
      }
      newX = Math.max(0, newX); // X не должен быть отрицательным после сдвига
      newWidth = Math.min(newWidth, displayed.width - newX); // Ограничиваем ширину с конечным X
    }

    // Ось Y
    if (handle.includes('n')) { // Верхняя граница
      const originalBottomY = this.resizeAreaStartY + this.resizeAreaStartHeight;
      newY = Math.max(0, newY); // Ограничиваем Y так, чтобы было >= 0
      // Ограничиваем Y так, чтобы высота не стала меньше minSize (относительно нижней границы)
      newY = Math.min(newY, originalBottomY - minSize);
      newHeight = originalBottomY - newY; // Пересчитываем высоту
    } else if (handle.includes('s')) { // Нижняя граница
      newHeight = Math.max(minSize, newHeight); // Высота должна быть не меньше minSize
      // Ограничиваем высоту так, чтобы newY + newHeight <= displayed.height
      newHeight = Math.min(newHeight, displayed.height - newY);
    } else { // Нет активного вертикального изменения (например, только 'w' или 'e' handle)
      newHeight = Math.max(minSize, newHeight);
      newHeight = Math.min(newHeight, displayed.height - newY);
      if (newY + newHeight > displayed.height) {
        newY = displayed.height - newHeight; // Сдвигаем Y вверх
      }
      newY = Math.max(0, newY); // Y не должен быть отрицательным после сдвига
      newHeight = Math.min(newHeight, displayed.height - newY); // Ограничиваем высоту с конечным Y
    }
    
    newWidth = Math.max(minSize, newWidth);
    newWidth = Math.min(newWidth, displayed.width - newX);
    
    newHeight = Math.max(minSize, newHeight);
    newHeight = Math.min(newHeight, displayed.height - newY);

    if (newX + newWidth > displayed.width) newX = displayed.width - newWidth;
    if (newY + newHeight > displayed.height) newY = displayed.height - newHeight;
    newX = Math.max(0, newX);
    newY = Math.max(0, newY);


    this.resizedArea.x(this.pixelToProportionalX(newX));
    this.resizedArea.y(this.pixelToProportionalY(newY));
    this.resizedArea.width(this.pixelToProportionalWidth(newWidth));
    this.resizedArea.height(this.pixelToProportionalHeight(newHeight));

    console.log('[IMAGE-CLICK-AREAS] applyResize - Resized Area (pixel):', 
                { newX, newY, newWidth, newHeight, handle: this.resizeHandle, deltaX, deltaY });
    console.log('[IMAGE-CLICK-AREAS] applyResize - Resized Area (proportional):', 
                { x: this.resizedArea.x(), y: this.resizedArea.y(), w: this.resizedArea.width(), h: this.resizedArea.height() });
  }

  handleResizeEnd() {
    if (!this.resizedArea) return;

    this.isResizing(false);

    document.removeEventListener('mousemove', this.boundHandleResizeMove);
    document.removeEventListener('mouseup', this.boundHandleResizeEnd);

    let px = this.resizedArea.x();
    let py = this.resizedArea.y();
    let pw = this.resizedArea.width();
    let ph = this.resizedArea.height();

    px = Math.max(0, Math.min(px, 1));
    py = Math.max(0, Math.min(py, 1));
    pw = Math.max(0.001, Math.min(pw, 1 - px)); 
    ph = Math.max(0.001, Math.min(ph, 1 - py));

    this.resizedArea.x(px);
    this.resizedArea.y(py);
    this.resizedArea.width(pw);
    this.resizedArea.height(ph);
    
    console.log('[IMAGE-CLICK-AREAS] handleResizeEnd - Final Proportional:', 
                { x: px, y: py, w: pw, h: ph });

    this.notifyAreasChange();
    this.resizedArea = null;
    this.resizeHandle = null;
  }

  deleteArea(areaToDelete) {
    if (!this.canEditArea(areaToDelete)) return;

    const wasSelected = (this.selectedArea() === areaToDelete);
    const areaIdToDelete = ko.unwrap(areaToDelete.id); // Get ID before removing for notification
    this.areas.remove(areaToDelete);
    
    if (wasSelected) {
      this.selectedArea(null);
      if (this.parentSelectedAreaObservable) {
        this.parentSelectedAreaObservable(null);
      }
    }
    this.notifyAreasChange();
  }

  notifyAreasChange() {
    if (typeof this.onAreasChange === 'function') {
      this.onAreasChange();
    }
  }

  dispose() {
    if (this.boundHandleDocumentMouseMove) {
      document.removeEventListener('mousemove', this.boundHandleDocumentMouseMove);
    }
    if (this.boundHandleDocumentMouseUp) {
      document.removeEventListener('mouseup', this.boundHandleDocumentMouseUp);
    }
    if (this.isContainerLocked && typeof this.isContainerLocked.dispose === 'function') {
      this.isContainerLocked.dispose();
    }

    super.dispose();
  }
}
